import {
  BlogPostsByAuthorParams,
  BlogPostsByCategoryParams,
  BlogPostsParams,
  StrapiBlogPost,
  StrapiBlogPostsResponse,
  StrapiCategoriesResponse,
  StrapiCategory,
  StrapiImage} from '@/@types/strapi'
import placeholderImg from '@/assets/img/placeholder.png'

import config from '../config'

class StrapiApi {
  private baseUrl = config.strapi.url

  /**
   * Helper method to add population parameters to URLSearchParams
   */
  private addPopulateParams(searchParams: URLSearchParams): void {
    searchParams.append('populate[photo]', 'true')
    searchParams.append('populate[categories]', 'true')
    searchParams.append('populate[author][populate][avatar]', 'true')
    searchParams.append('populate[relatedPosts]', 'true')
    searchParams.append('populate[relatedPosts][populate][categories]', 'true')
    searchParams.append('populate[relatedPosts][populate][photo]', 'true')
    searchParams.append('populate[relatedPosts][populate][author][populate][avatar]', 'true')
  }

  /**
   * @param {string} documentId
   * @returns {Promise<StrapiBlogPost>}
   */
  async getBlogPostById(documentId: string): Promise<StrapiBlogPost> {
    const searchParams = new URLSearchParams()

    // Add populate parameters
    this.addPopulateParams(searchParams)

    const url = `${this.baseUrl}/blog-posts/${documentId}?${searchParams.toString()}`

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${config.strapi.bearerToken}`
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const responseData = await response.json()
      return responseData.data
    } catch (error) {
      console.error('Error fetching blog post:', error)
      throw error
    }
  }

  /**
   * @param {string} slug
   * @returns {Promise<StrapiBlogPost>}
   */
  async getBlogPostBySlug(slug: string): Promise<StrapiBlogPost> {
    const searchParams = new URLSearchParams()
    searchParams.append('filters[slug][$eq]', slug)
    this.addPopulateParams(searchParams)

    const url = `${this.baseUrl}/blog-posts?${searchParams.toString()}`

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${config.strapi.bearerToken}`
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const responseData = await response.json()
      const typedResponse = responseData as StrapiBlogPostsResponse
      if (!typedResponse.data || typedResponse.data.length === 0) {
        throw new Error(`Blog post not found for slug: ${slug}`)
      }
      return typedResponse.data[0]
    } catch (error) {
      console.error('Error fetching blog post by slug:', error)
      throw error
    }
  }

  /**
   * @param {BlogPostsParams} params
   * @param {string[]} params.documentIds
   * @param {number} [params.limit=5]
   * @returns {Promise<StrapiBlogPost[]>}
   */
  async getBlogPostsByIds(params: BlogPostsParams): Promise<StrapiBlogPost[]> {
    const { documentIds, limit = 5 } = params
    const sort = 'createdAt:desc'

    // Build query parameters
    const searchParams = new URLSearchParams()

    // Add document ID filters
    documentIds.forEach((id, index) => {
      searchParams.append(`filters[documentId][$in][${index}]`, id)
    })

    // Add sort
    searchParams.append('sort', sort)

    // Add pagination
    searchParams.append('pagination[limit]', limit.toString())

    // Add populate parameters
    this.addPopulateParams(searchParams)

    const url = `${this.baseUrl}/blog-posts?${searchParams.toString()}`

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${config.strapi.bearerToken}`
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const responseData = await response.json()
      const typedResponse = responseData as StrapiBlogPostsResponse
      return typedResponse.data
    } catch (error) {
      console.error('Error fetching blog posts:', error)
      throw error
    }
  }

  /**
   * @param {BlogPostsByCategoryParams} params
   * @param {string} params.categorySlug
   * @param {number} [params.limit=5]
   * @returns {Promise<StrapiBlogPost[]>}
   */
  async getBlogPostsByCategory(params: BlogPostsByCategoryParams): Promise<StrapiBlogPost[]> {
    const { categorySlug, limit = 5 } = params
    const sort = 'createdAt:desc'

    // Build query parameters
    const searchParams = new URLSearchParams()

    // Add category filter
    searchParams.append('filters[categories][slug][$eq]', categorySlug)

    // Add sort
    searchParams.append('sort', sort)

    // Add pagination
    searchParams.append('pagination[limit]', limit.toString())

    // Add populate parameters
    this.addPopulateParams(searchParams)

    const url = `${this.baseUrl}/blog-posts?${searchParams.toString()}`

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${config.strapi.bearerToken}`
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const responseData = await response.json()
      const typedResponse = responseData as StrapiBlogPostsResponse
      return typedResponse.data
    } catch (error) {
      console.error('Error fetching blog posts by category:', error)
      throw error
    }
  }

  /**
   * @param {BlogPostsByAuthorParams} params
   * @param {string} params.authorDocumentId
   * @param {number} [params.limit=50]
   * @returns {Promise<StrapiBlogPost[]>}
   */
  async getBlogPostsByAuthor(params: BlogPostsByAuthorParams): Promise<StrapiBlogPost[]> {
    const { authorDocumentId, limit = 50 } = params
    const sort = 'createdAt:desc'

    const searchParams = new URLSearchParams()
    searchParams.append('filters[author][documentId][$eq]', authorDocumentId)
    searchParams.append('sort', sort)
    searchParams.append('pagination[limit]', limit.toString())
    this.addPopulateParams(searchParams)

    const url = `${this.baseUrl}/blog-posts?${searchParams.toString()}`

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${config.strapi.bearerToken}`
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const responseData = await response.json()
      const typedResponse = responseData as StrapiBlogPostsResponse
      return typedResponse.data
    } catch (error) {
      console.error('Error fetching blog posts by author:', error)
      throw error
    }
  }

  /**
   * @returns {Promise<StrapiCategory[]>}
   */
  async getCategories(): Promise<StrapiCategory[]> {
    const url = `${this.baseUrl}/categories`

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${config.strapi.bearerToken}`
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const responseData = await response.json()
      const typedResponse = responseData as StrapiCategoriesResponse
      return typedResponse.data
    } catch (error) {
      console.error('Error fetching categories:', error)
      throw error
    }
  }

  /**
   * @param {string} search
   * @param {number} [params.limit=5]
   * @returns {Promise<StrapiBlogPost[]>}
   */
  async getBlogPostsBySearch(search: string, limit = 5): Promise<StrapiBlogPost[]> {
    const sort = 'createdAt:desc'
    const searchParams = new URLSearchParams()

    // Add $or filters for title, excerpt, and content
    searchParams.append('filters[$or][0][title][$containsi]', search)
    searchParams.append('filters[$or][1][excerpt][$containsi]', search)
    searchParams.append('filters[$or][2][content][$containsi]', search)
    searchParams.append('filters[$or][3][author][name][$containsi]', search)
    searchParams.append('filters[$or][4][header][$containsi]', search)

    // Add sort
    searchParams.append('sort', sort)

    // Add pagination
    searchParams.append('pagination[limit]', limit.toString())

    // Add populate parameters
    this.addPopulateParams(searchParams)

    const url = `${this.baseUrl}/blog-posts?${searchParams.toString()}`

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${config.strapi.bearerToken}`
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const responseData = await response.json()
      const typedResponse = responseData as StrapiBlogPostsResponse
      return typedResponse.data
    } catch (error) {
      console.error('Error searching blog posts:', error)
      throw error
    }
  }

  /**
   * Helper function to replace Strapi image URLs with placeholder
   * This is a temporary override until AWS/S3 bucket is online for Strapi
   */
  private replaceImageWithPlaceholder(image: StrapiImage | null): StrapiImage | null {
    if (!image) {return null}

    return {
      ...image,
      url: placeholderImg,
      formats: {
        ...image.formats,
        thumbnail: image.formats.thumbnail
          ? {
              ...image.formats.thumbnail,
              url: placeholderImg
            }
          : undefined,
        small: image.formats.small
          ? {
              ...image.formats.small,
              url: placeholderImg
            }
          : undefined,
        medium: image.formats.medium
          ? {
              ...image.formats.medium,
              url: placeholderImg
            }
          : undefined,
        large: image.formats.large
          ? {
              ...image.formats.large,
              url: placeholderImg
            }
          : undefined
      }
    }
  }

  /**
   * Helper function to transform blog post data with placeholder images
   */
  private transformBlogPostWithPlaceholder(post: StrapiBlogPost): StrapiBlogPost {
    return {
      ...post,
      photo: this.replaceImageWithPlaceholder(post.photo),
      relatedPosts: post.relatedPosts.map((relatedPost) => ({
        ...relatedPost,
        photo: this.replaceImageWithPlaceholder(relatedPost.photo)
      }))
    }
  }
}

export default new StrapiApi()
